import { useMemo } from "react";
import { Field, reduxForm, SubmissionError } from "redux-form";
import { Button, Checkbox, Input } from "expano-components";
import useCreate from "hooks/useCreate";
import useGet from "hooks/useGet";
import useUpdate from "hooks/useUpdate";
import { Dashboard, Select } from "components";

const CheckField = (props) => <Checkbox {...props} />;
const InputField = (props) => <Input {...props} />;

const normalizeId = (value) => {
  if (value === null || value === undefined || value === "") return null;

  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const ShippingRateSelect = ({ input, options, meta, label }) => (
  <Select
    label={label}
    meta={meta}
    isClearable
    options={options}
    value={
      options.find(
        ({ value }) => normalizeId(value) === normalizeId(input.value)
      ) || null
    }
    handleChange={(value) => input.onChange(normalizeId(value))}
  />
);

const BasicData = ({
  id,
  identifier,
  type,
  platform,
  refetch,
  submitting,
  handleSubmit,
  pristine,
}) => {
  const { data: shipping_rates, refetch: refetchShippingRates } = useGet({
    query: ["channel_shipping_rates", id],
    path: "/channel_shipping_rates",
    search: {
      "q[channel_id_eq]": id,
      per_page: 1000,
    },
    options: {
      enabled: !!id,
    },
  });

  const shipping_rate_options = useMemo(() => {
    if (!shipping_rates?.length) return [];

    return shipping_rates.map(({ id: rate_id, name }) => ({
      label: name,
      value: rate_id,
    }));
  }, [shipping_rates]);

  const { is_creating: is_syncing, create: syncShippingRates } = useCreate({
    path: `/channels/${id}/sync_shipping_rates`,
    success_message: "Cenniki zostały zsynchronizowane",
    onSuccess: () => refetchShippingRates(),
  });

  const { is_updating, update } = useUpdate({
    path: `/channels/${id}`,
    onSuccess: refetch,
    onError: (err) => {
      throw new SubmissionError(err);
    },
  });

  const onSubmit = (values) =>
    update({
      ...values,
      additional_channel_shipping_price:
        values.additional_channel_shipping_price === "" ||
        values.additional_channel_shipping_price == null
          ? 0
          : values.additional_channel_shipping_price,
    });

  return (
    <Dashboard
      header={{
        icon: "info",
        title: "Podstawowe dane",
      }}
      footer={
        <Button
          type="save"
          text="Zapisz"
          disabled={pristine || submitting || is_updating}
          onClick={handleSubmit(onSubmit)}
        />
      }
    >
      <form
        className="dashboard__form"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        <div className="row m-b-20">
          <div className="box-6">
            <Input
              meta={{}}
              input={{ value: identifier, readOnly: true, name: "identifier" }}
              label="Identyfikator"
              readOnly
              type="text"
            />
          </div>
          <div className="box-6">
            <Input
              meta={{}}
              input={{ value: type, readOnly: true, name: "type" }}
              label="Typ kanału"
              readOnly
              type="text"
            />
          </div>
        </div>
        <div className="row m-b-20">
          <div className="box-6">
            <Field
              name="additional_channel_shipping_price"
              label="Kwota wysyłki doliczana do ceny produktu"
              component={InputField}
              type="number"
              step="0.01"
              min="0"
              postfix={platform?.currency_code}
            />
          </div>
          <div className="box-6">
            <Field
              name="free_channel_shipping_rate_id"
              label="Cennik darmowej wysyłki"
              component={ShippingRateSelect}
              options={shipping_rate_options}
            />
            <button
              type="button"
              className="btn btn-sm btn-blue m-b-10"
              disabled={is_syncing}
              onClick={() => syncShippingRates({})}
            >
              <span>{is_syncing ? "Proszę czekać..." : "Synchronizuj"}</span>
            </button>
          </div>
        </div>
        <p className="heading">
          Ustawienia tworzenia produktów na kanale:
        </p>
        <div className="row">
          <div className="box-12">
            <Field
              name="ignore_bundle_products"
              label="Ignoruj zestawy produktów (bundle)"
              component={CheckField}
            />
            <Field
              name="ignore_virtual_products"
              label="Ignoruj produkty wirtualne"
              component={CheckField}
            />
          </div>
        </div>
      </form>
    </Dashboard>
  );
};

export default reduxForm({
  form: "edit-channel",
  enableReinitialize: true,
})(BasicData);
